import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@playbooks/ui/components/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { Field, FieldDescription, FieldLabel } from '@playbooks/ui/components/field';
import { Input } from '@playbooks/ui/components/input';
import { Textarea } from '@playbooks/ui/components/textarea';
import { ToggleGroup, ToggleGroupItem } from '@playbooks/ui/components/toggle-group';

import { useStore, useWorkspace } from 'contexts';
import { parseAgentSkillMarkdown } from 'utils/agent-skill-markdown';

const AgentSkillFlowForm = ({ model, onPrev, onNext, onCreate, onUpdate, projectLocal = false }) => {
	const [url, setUrl] = useState('');
	const [preview, setPreview] = useState<any>(null);
	const [tags, setTags] = useState<any[]>([]);
	const [skill, setSkill] = useState<any>({
		name: '',
		description: '',
		markdown: '',
		visibility: 'private',
		tagIds: [],
	});
	const [previewing, setPreviewing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const fileRef = useRef(null);
	const store = useStore();
	const workspace = useWorkspace();
	const parsed = useMemo(() => parseAgentSkillMarkdown(skill.markdown), [skill.markdown]);

	useEffect(() => {
		store.query({ url: '/agent-skill-tags' }).then(response => setTags(response.data || []));
	}, []);
	useEffect(() => {
		if (!model) {
			setUrl('');
			setPreview(null);
			setSkill({ name: '', description: '', markdown: '', visibility: 'private', tagIds: [] });
			return;
		}
		setUrl(model.sourceUrl || '');
		setPreview(model);
		setSkill({
			name: model.name || '',
			description: model.description || '',
			markdown: model.markdown || '',
			visibility: model.visibility || 'private',
			tagIds: (model.tags || []).map(tag => tag.id),
		});
	}, [model]);

	const onPreview = async () => {
		setPreviewing(true);
		setError('');
		try {
			const response = await store.createRecord({
				url: '/workspace/agent-skill-previews/github',
				data: { url: url.trim() },
			});
			setPreview(response.data);
			setSkill(current => ({
				...current,
				name: response.data.name || '',
				description: response.data.description || '',
				markdown: response.data.markdown || '',
			}));
		} catch (nextError) {
			setError(nextError?.message || 'The skill could not be imported.');
		} finally {
			setPreviewing(false);
		}
	};
	const onRefresh = async () => {
		setPreviewing(true);
		setError('');
		try {
			const response = await store.createRecord({
				url: `/workspace/skills/${model.uuid}/refresh-preview`,
				data: {},
			});
			setPreview(response.data);
			setSkill(current => ({
				...current,
				name: response.data.name || '',
				description: response.data.description || '',
				markdown: response.data.markdown || '',
			}));
		} catch (nextError) {
			setError(nextError?.message || 'The skill could not be refreshed.');
		} finally {
			setPreviewing(false);
		}
	};
	const readFile = async file => {
		if (!file) return;
		if (!file.name.toLowerCase().endsWith('.md')) return setError('Please choose a Markdown (.md) file.');
		const markdown = await file.text();
		setPreview(null);
		setSkill(current => ({ ...current, markdown }));
		setError('');
	};
	const onSave = async event => {
		event.preventDefault();
		if (parsed.errors.length) return;
		setSaving(true);
		setError('');
		try {
			const response = model
				? onUpdate
					? { data: await onUpdate(skill) }
					: preview?.previewId
						? await store.updateRecord({
								url: `/workspace/skills/${model.uuid}/refresh`,
								data: { previewId: preview.previewId, ...skill },
							})
						: await store.updateRecord({ url: `/agent-skills/${model.uuid}`, data: skill })
				: onCreate
					? { data: await onCreate({ ...skill, sourceType: preview?.previewId ? 'import' : 'manual' }) }
					: preview?.previewId
						? await store.createRecord({
								url: '/workspace/skills/imports',
								data: { previewId: preview.previewId, ...skill },
							})
						: await store.createRecord({ url: '/agent-skills', data: { ...skill, sourceType: 'manual' } });
			await onNext(response.data);
		} catch (nextError) {
			setError(nextError?.message || 'The skill could not be saved.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<form onSubmit={onSave}>
			<DialogHeader>
				<DialogTitle>{model ? 'Edit skill' : 'Add a skill'}</DialogTitle>
			</DialogHeader>
			<div className='grid gap-5'>
				{!model ? (
					<Field>
						<FieldLabel>Import from GitHub</FieldLabel>
						<div className='flex gap-2'>
							<Input
								type='url'
								value={url}
								placeholder='https://github.com/owner/repo/blob/main/SKILL.md'
								onChange={event => setUrl(event.target.value)}
							/>
							<Button type='button' isLoading={previewing} disabled={!url.trim()} onClick={onPreview}>
								Import
							</Button>
						</div>
						<FieldDescription>
							Imports a public, self-contained SKILL.md into the editable fields below.
						</FieldDescription>
					</Field>
				) : null}
				{model?.sourceType === 'import' && model.sourceUrl ? (
					<Field>
						<FieldLabel>Source</FieldLabel>
						<div className='flex gap-2'>
							<Input value={model.sourceUrl} readOnly />
							<Button type='button' variant='secondary' isLoading={previewing} onClick={onRefresh}>
								Refresh
							</Button>
						</div>
						<FieldDescription>Refresh loads upstream content without saving it.</FieldDescription>
					</Field>
				) : null}
				<Field>
					<FieldLabel>Owner</FieldLabel>
					<Input
						value={
							projectLocal ? 'Current project' : model?.owner?.name || workspace.workspace?.name || 'Current workspace'
						}
						readOnly
					/>
				</Field>
				<FieldDescription>
					{projectLocal
						? 'This skill is local to this project and does not change its workspace source.'
						: 'This skill is private to the workspace. Marketplace publishing is curated by Playbooks.'}
				</FieldDescription>
				<Field>
					<FieldLabel>Tags</FieldLabel>
					<ToggleGroup
						value={(skill.tagIds || []).map(String)}
						onValueChange={values => setSkill({ ...skill, tagIds: values.map(Number) })}
						className='flex w-full flex-wrap justify-start'>
						{tags.map(tag => (
							<ToggleGroupItem key={tag.id} value={String(tag.id)}>
								{tag.name}
							</ToggleGroupItem>
						))}
					</ToggleGroup>
					<FieldDescription>Curated labels help people and agents discover this skill.</FieldDescription>
				</Field>
				<Field>
					<FieldLabel>Name</FieldLabel>
					<Input value={skill.name} onChange={event => setSkill({ ...skill, name: event.target.value })} />
				</Field>
				<Field>
					<FieldLabel>Description</FieldLabel>
					<Textarea
						rows={3}
						value={skill.description}
						onChange={event => setSkill({ ...skill, description: event.target.value })}
					/>
				</Field>
				<Field>
					<div className='flex-between'>
						<FieldLabel>SKILL.md</FieldLabel>
						<Button type='button' variant='ghost' size='sm' onClick={() => fileRef.current?.click()}>
							Choose file
						</Button>
					</div>
					<Textarea
						rows={14}
						value={skill.markdown}
						onChange={event => setSkill({ ...skill, markdown: event.target.value })}
						className='min-h-72 resize-y font-mono text-xs'
					/>
					<input
						ref={fileRef}
						type='file'
						accept='.md,text/markdown,text/plain'
						className='sr-only'
						onChange={event => readFile(event.target.files?.[0])}
					/>
				</Field>
				{[...parsed.errors, error].filter(Boolean).map(message => (
					<p key={message} className='text-sm text-danger'>
						{message}
					</p>
				))}
			</div>
			<DialogFooter>
				<Button type='button' variant='secondary' onClick={onPrev}>
					Back
				</Button>
				<Button type='submit' isLoading={saving} disabled={Boolean(parsed.errors.length)}>
					Save
				</Button>
			</DialogFooter>
		</form>
	);
};

export { AgentSkillFlowForm };
